import React, { useEffect, useMemo, useState } from "react"
import CategorySelector from "../BrandComparison/CategorySelector"
import { useAIComparison } from "./hooks/useAIComparison"
import TableVisualization from "~/ui-rtk/components/ui/charts/TableVisualization"
import {
  TableVisualizationColumn,
  TableColumnType,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { PaginationState } from "@tanstack/react-table"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"

const defaultPagination: PaginationState = {
  pageIndex: 0,
  pageSize: 100,
}

const AIModelTable: React.FC<{ dateRange: [Date, Date] }> = ({ dateRange }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const currentCompany = useCurrentCompany()

  const { models, companies, scoreMap, categories } = useAIComparison({
    selectedCategory,
    dateRange,
  })

  // Set initial category
  useEffect(() => {
    if (!selectedCategory && categories.length > 0) {
      setSelectedCategory(categories[0])
    }
  }, [categories, selectedCategory])

  // Get competitors list to determine AI suggested companies
  const competitors = useMemo(
    () =>
      [
        ...(currentCompany?.metadata?.keywords?.google?.competitors || []),
        currentCompany?.name,
      ]
        .filter(Boolean)
        .map(c => c.toLowerCase()),
    [
      currentCompany?.metadata?.keywords?.google?.competitors,
      currentCompany?.name,
    ],
  )

  // Transform data for TableVisualization
  const tableData = useMemo(() => {
    if (!companies.length || !models.length) return []

    return companies.map(company => {
      const rowData: Record<string, unknown> = {
        company_name: company,
      }
      models.forEach(model => {
        rowData[model] = scoreMap[company]?.[model] ?? null
      })
      return rowData
    })
  }, [companies, models, scoreMap])

  // Configure columns for TableVisualization
  const columnConfig = useMemo(() => {
    if (!models.length) return []

    const config: TableVisualizationColumn[] = [
      {
        key: "company_name",
        header: "Company",
        type: "title" as TableColumnType,
        width: 220,
        render: (row: Record<string, unknown>) => {
          const companyName = row.company_name as string
          const isAIGenerated = !competitors.includes(companyName.toLowerCase())

          return (
            <div className="flex items-center justify-between">
              <span className="capitalize">{companyName}</span>
              {isAIGenerated && (
                <span className="text-[10px] font-medium bg-pink/20 text-pink-300 px-system-xs py-system-xxs rounded">
                  AI Suggested
                </span>
              )}
            </div>
          )
        },
      },
      ...models.map(model => ({
        key: model,
        header: model,
        type: "number" as TableColumnType,
        render: (row: Record<string, unknown>) => {
          const value = row[model]
          return (value || "-") as React.ReactNode
        },
      })),
    ]
    return config
  }, [models, competitors])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-system-md w-full">
        <h2 className="text-xl font-semibold text-white">AI Model Analysis</h2>
      </div>

      <CategorySelector
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />

      <TableVisualization
        metrics={tableData as TMetric[]}
        chartId="ai-model-comparison"
        columnConfig={columnConfig}
        paginationState={defaultPagination}
        enablePagination={false}
      />
    </div>
  )
}

export default AIModelTable
